/**
 * WS transport for the reducer-only client (M8-B).
 *
 * The seam is browser-style (`addEventListener`) because `ws` v8 also exposes
 * it — so one implementation serves both the browser bundle and the headless
 * full-stack smoke (which injects `ws`'s WebSocket via `WebSocketImpl`).
 */
import { parseReplayDecksMsg, type ClientMsg, type ServerMsg } from '../../server/protocol.js';

/** Minimal structural WebSocket surface shared by the browser and `ws` v8. */
export interface WebSocketLike {
  send(data: string): void;
  close(): void;
  readonly readyState: number;
  addEventListener(type: 'open', listener: () => void): void;
  addEventListener(type: 'close', listener: (ev: { code?: number; reason?: string }) => void): void;
  addEventListener(type: 'message', listener: (ev: { data: unknown }) => void): void;
  addEventListener(type: 'error', listener: (ev: unknown) => void): void;
}

export type WebSocketCtor = new (url: string) => WebSocketLike;

export interface Transport {
  send(msg: ClientMsg): void;
  close(): void;
  readonly isOpen: boolean;
}

export interface ConnectOptions {
  /** Full WS URL, e.g. `ws://host:port/ws?room=r1&playerId=A`. */
  url: string | (() => string);
  onMessage: (msg: ServerMsg) => void;
  onOpen?: () => void;
  onClose?: (code: number, reason: string) => void;
  onReconnecting?: (code: number, reason: string, delayMs: number) => void;
  /** Returns true when a page replacement handled the stale connection. */
  beforeReconnect?: () => Promise<boolean>;
  onError?: (err: unknown) => void;
  /** Called only when this physical connection was rejected before a session ack. */
  onRejectedHandshake?: (attemptUrl: string) => void;
  /** Finite retry schedule for abnormal disconnects. Empty disables reconnect. */
  reconnectDelaysMs?: readonly number[];
  /** Inject a WebSocket constructor (default: browser global). */
  WebSocketImpl?: WebSocketCtor;
}

/** WebSocket.OPEN — a fixed constant in the protocol, safe to inline. */
const OPEN = 1;
const DEFAULT_RECONNECT_DELAYS_MS = [250, 500, 1_000, 2_000, 4_000, 8_000] as const;

const decoder = new TextDecoder();

/**
 * Coerce an incoming frame payload to text. A `ws` text frame usually arrives
 * as a string, but may be a Buffer/ArrayBuffer if binaryType differs; the
 * browser delivers a string (or ArrayBuffer). Buffer and typed arrays are
 * `ArrayBuffer.isView`, so TextDecoder covers UTF-8 decoding for all of them.
 */
function coerceToText(data: unknown): string {
  if (typeof data === 'string') return data;
  if (data instanceof ArrayBuffer) return decoder.decode(data);
  if (ArrayBuffer.isView(data)) return decoder.decode(data);
  return String(data);
}

export function connectWebSocket(opts: ConnectOptions): Transport {
  // Localized interop cast: the global WebSocket is untyped on `globalThis`.
  const WS: WebSocketCtor | undefined =
    opts.WebSocketImpl ?? (globalThis as { WebSocket?: WebSocketCtor }).WebSocket;
  if (WS === undefined) {
    throw new Error('connectWebSocket: нет доступного WebSocket (передайте WebSocketImpl)');
  }
  const WebSocketConstructor = WS;

  const url = typeof opts.url === 'function' ? opts.url : () => opts.url as string;
  const reconnectDelays = opts.reconnectDelaysMs ?? DEFAULT_RECONNECT_DELAYS_MS;
  if (reconnectDelays.some((delay) => !Number.isSafeInteger(delay) || delay < 0)) {
    throw new Error('reconnectDelaysMs должны содержать неотрицательные safe integer');
  }
  let socket: WebSocketLike;
  let stopped = false;
  let retryIndex = 0;
  let retryTimer: ReturnType<typeof setTimeout> | undefined;

  function connect(): void {
    let attemptUrl: string;
    try {
      attemptUrl = url();
    } catch (err) {
      opts.onError?.(err);
      return;
    }
    const attempt = new WebSocketConstructor(attemptUrl);
    socket = attempt;
    let receivedSession = false;
    let terminalHandshakeRejection = false;

    attempt.addEventListener('open', () => opts.onOpen?.());
    attempt.addEventListener('message', (ev) => {
      try {
        const parsed = JSON.parse(coerceToText(ev.data)) as unknown;
        const msg = typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)
          && (parsed as { t?: unknown }).t === 'replay_decks'
          ? parseReplayDecksMsg(parsed)
          : parsed as ServerMsg;
        if (msg.t === 'session') {
          receivedSession = true;
          retryIndex = 0;
        } else if (msg.t === 'rejected' && msg.clientSeq === -1 && !receivedSession) {
          terminalHandshakeRejection = msg.code === 'INVALID_SESSION'
            || msg.code === 'SERVER_UPDATED'
            || msg.code === 'PROTOCOL_MISMATCH';
          opts.onRejectedHandshake?.(attemptUrl);
        }
        opts.onMessage(msg);
      } catch (err) {
        opts.onError?.(err);
      }
    });
    attempt.addEventListener('close', (ev) => {
      const code = ev.code ?? 1000;
      const reason = ev.reason ?? '';
      if (stopped || socket !== attempt) return;
      if (code === 1000 || code === 4001) {
        opts.onClose?.(code, reason);
        return;
      }
      if (terminalHandshakeRejection) {
        opts.onClose?.(code, reason);
        return;
      }
      let nextUrl: string;
      try {
        nextUrl = url();
      } catch (err) {
        opts.onError?.(err);
        return;
      }
      // A policy rejection is retryable only when its rejected credential was
      // synchronously cleared and therefore the next attempt URL is different.
      if (code === 1008 && nextUrl === attemptUrl) {
        opts.onClose?.(code, reason);
        return;
      }
      const scheduleReconnect = (): void => {
        if (stopped || socket !== attempt) return;
        const delay = reconnectDelays[retryIndex];
        if (delay === undefined) {
          opts.onClose?.(code, reason);
          return;
        }
        retryIndex += 1;
        opts.onReconnecting?.(code, reason, delay);
        retryTimer = setTimeout(() => {
          retryTimer = undefined;
          if (!stopped) connect();
        }, delay);
      };
      if (opts.beforeReconnect === undefined) {
        scheduleReconnect();
        return;
      }
      let refreshAttempt: Promise<boolean>;
      try {
        refreshAttempt = opts.beforeReconnect();
      } catch (err) {
        opts.onError?.(err);
        scheduleReconnect();
        return;
      }
      void refreshAttempt.then((handled) => {
        if (handled) {
          stopped = true;
          return;
        }
        scheduleReconnect();
      }, (err: unknown) => {
        opts.onError?.(err);
        scheduleReconnect();
      });
    });
    attempt.addEventListener('error', (ev) => opts.onError?.(ev));
  }

  connect();

  return {
    send(msg) {
      socket.send(JSON.stringify(msg));
    },
    close() {
      stopped = true;
      if (retryTimer !== undefined) clearTimeout(retryTimer);
      socket.close();
    },
    get isOpen() {
      return socket.readyState === OPEN;
    },
  };
}
